import type { EChartsOption } from 'echarts';
import { Chart, ContainerSize } from '../categoricalCharts/EChart';
import { GaugeWidgetProps } from './types';
import { formatNumber } from '@ardoq/locale';
import { colors } from '@ardoq/design-tokens';
import {
  commonChartConfig,
  DEFAULT_CHART_PRIMARY_COLOR,
} from '../common-chart-config';
import { type I18nLocale, t } from '@ardoq/i18n';

const OUTER_RADIUS_RATIO = 0.85;
const INNER_RADIUS_RATIO = 0.55;
const INNER_RADIUS = `${INNER_RADIUS_RATIO * 100}%`;
const OUTER_RADIUS = `${OUTER_RADIUS_RATIO * 100}%`;
const PIE_CENTER_Y_RATIO = 0.65;

const gaugeLayout = {
  startAngle: 180,
  endAngle: 360,
  radius: [INNER_RADIUS, OUTER_RADIUS],
  center: ['50%', `${PIE_CENTER_Y_RATIO * 100}%`],
  label: { show: false },
  labelLine: { show: false },
};

const remainingSegmentStyle = { color: 'transparent' };
const remainingSegmentEmphasis = { disabled: true };

type GaugeChartDataItem = {
  name: string;
  value: number;
  isOtherSegment: boolean;
  itemStyle: { color: string; borderRadius?: number };
  emphasis?: { disabled: boolean };
};

const getGaugeChartData = (percentage: number): GaugeChartDataItem[] => [
  {
    name: 'Matching',
    value: percentage,
    isOtherSegment: false,
    itemStyle: {
      color: DEFAULT_CHART_PRIMARY_COLOR,
      borderRadius: 4,
    },
  },
  {
    name: 'Remaining',
    value: Math.max(0, 1 - percentage),
    isOtherSegment: true,
    itemStyle: remainingSegmentStyle,
    emphasis: remainingSegmentEmphasis,
  },
];

type GaugeChartOptionsParams = {
  percentage: number;
  formattedPercentage: string;
  formattedNumerator: string;
  formattedDenominator: string;
  hasClickHandler: boolean;
  i18nLocale: I18nLocale;
};

const getGaugeChartOptions = ({
  percentage,
  formattedPercentage,
  formattedNumerator,
  formattedDenominator,
  hasClickHandler,
  i18nLocale,
}: GaugeChartOptionsParams): EChartsOption => ({
  ...commonChartConfig,
  tooltip: { show: false },
  series: [
    {
      type: 'pie',
      ...gaugeLayout,
      data: [{ value: 1, itemStyle: { color: colors.grey200 } }],
      itemStyle: { borderWidth: 0, borderRadius: 4 },
      silent: true,
    },
    {
      type: 'pie',
      ...gaugeLayout,
      data: getGaugeChartData(percentage),
      itemStyle: { borderWidth: 0 },
      cursor: hasClickHandler ? 'pointer' : 'default',
    },
  ],
  title: [
    {
      id: 'gauge-percentage',
      show: true,
      text: formattedPercentage,
      left: 'center',
      textStyle: {
        fontWeight: 600,
        color: colors.grey950,
      },
    },
    {
      id: 'gauge-ratio',
      show: true,
      text: t(
        i18nLocale,
        'dashboard:gaugeWidget.outOf',
        { numerator: formattedNumerator, denominator: formattedDenominator },
        '{{numerator}} out of {{denominator}}'
      ),
      left: 'center',
      textStyle: {
        color: colors.grey500,
        fontWeight: 'normal',
      },
    },
  ],
});

const getGaugeResizeOptions = ({
  width,
  height,
}: ContainerSize): EChartsOption => {
  // echarts computes pie radius from min(width, height) / 2 * percentage,
  // so mirror that here to derive a radius in pixels for font and layout
  const chartRadius = Math.min(width, height) / 2;
  const outerRadius = OUTER_RADIUS_RATIO * chartRadius;
  const innerRadius = INNER_RADIUS_RATIO * chartRadius;
  const titleFontSize = Math.max(Math.min(Math.floor(outerRadius / 3), 40), 16);
  const subtextFontSize = Math.max(
    Math.min(Math.floor(outerRadius / 6), 14),
    10
  );
  const gap = Math.max(Math.round(titleFontSize * 0.25), 4);

  // Percentage sits inside the upper semicircle, just above the flat diameter,
  // and the ratio subtext sits below the flat diameter
  const pieCenterY = height * PIE_CENTER_Y_RATIO;
  const arcClearance = Math.max(innerRadius * 0.1, 4);
  const percentageTop = Math.max(pieCenterY - titleFontSize - arcClearance, 0);
  const ratioTop = pieCenterY + gap;

  return {
    title: [
      {
        id: 'gauge-percentage',
        top: percentageTop,
        textStyle: { fontSize: titleFontSize },
      },
      {
        id: 'gauge-ratio',
        top: ratioTop,
        textStyle: { fontSize: subtextFontSize },
      },
    ],
  };
};

export const GaugeWidget = (props: GaugeWidgetProps) => {
  const { numerator, denominator, userLocale, clickHandler, i18nLocale } =
    props;

  const percentage = Math.min(1, numerator / denominator);

  const chartOptions = getGaugeChartOptions({
    percentage,
    formattedPercentage: formatNumber(percentage, 'percentage', userLocale),
    formattedNumerator: formatNumber(numerator, 'integer', userLocale),
    formattedDenominator: formatNumber(denominator, 'integer', userLocale),
    hasClickHandler: Boolean(clickHandler),
    i18nLocale,
  });

  return (
    <Chart
      chartOptions={chartOptions}
      clickHandler={clickHandler}
      getResizeOptions={getGaugeResizeOptions}
    />
  );
};
